import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRawSf009FormData } from "./sf009FormData";
import {
  formatSf009DateDisplay,
  formatSf009DateDisplayOrDash,
  parseSf009DateForPicker,
  datePickerToSf009DateString,
} from "./sf009ImportDateUtils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import { applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import onsiteproLogo from "../../assets/logo2 2.png";
import CheckTick from "../../assets/icons/Check-Tick.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";
import { drawPdfCrossIcon, drawSignatureImageInPdfCell, isSigImage, toSigDataUrl } from "./sf009FormSignatureUtils";
import Sf009SignatureDisplay from "./Sf009SignatureDisplay";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const PDF_WHITE = [255, 255, 255];
const PDF_PAGE_BOTTOM = 32;
const PDF_EXTRA_BLANK_ROWS = 2;
const API_PATH = "/api/sf-009-h-electrical-audit-inspection-report";

const AUDIT_CHECKLIST_TEMPLATE = [
  { itemNo: 1, aspect: "Risk assessments", scope: "Use of risk assessments for work including initial site safety risk assessment" },
  { itemNo: 2, aspect: "Method statements", scope: "Are method statements in place and comprehensive?" },
  { itemNo: 3, aspect: "Signs and notices", scope: "Identification of electrical hazards, with restrictions well sign-posted" },
  { itemNo: 4, aspect: "Task management", scope: "Use of toolbox talks. Date of last Electrical Tool box Talk" },
  { itemNo: 5, aspect: "Switch room Safety Log", scope: "Up to date and being used" },
  { itemNo: 6, aspect: "Control of Restricted Areas", scope: "Use of barriers and signs & Limitation of Access" },
  { itemNo: 7, aspect: "Personnel Competency", scope: "Are competency checks being carried out and recorded" },
  {
    itemNo: 8,
    aspect: "Permits, Sanctions, Locking off Procedures and Electrical safety",
    scope: "Application of Electrical Safety Rules, permits to Work, sanctions to test,",
  },
  {
    itemNo: 9,
    aspect: "Installation and Equipment",
    scope: "Is the Electrical Installation and Equipment of good Standard and to specification",
  },
  { itemNo: 10, aspect: "Electrical Inspection Reports", scope: "Carried out on a regular basis by Authorised Person" },
  {
    itemNo: 11,
    aspect: "Construction completion",
    scope: "Is the Electrical section of the Construction Completion Commissioning File up to date?",
  },
  {
    itemNo: 12,
    aspect: "CDM'15 Construction Phase Plan",
    scope: "Electrical information is in the site's Construction Phase Health and Safety Plan",
  },
  {
    itemNo: 13,
    aspect: "All site Electrical Portable appliance's",
    scope:
      "Have all portable appliances been inspected and tested in accordance with the code of practice for In Service Inspection and Testing of Electrical Equipment (IET)",
  },
  {
    itemNo: 14,
    aspect: "Site accommodation offices toilets etc.",
    scope:
      "Have these been inspected and tested to verify compliance with BS7671 and section 6 of SP-006 TTSL LV Electrical Safety Rules",
  },
  {
    itemNo: 15,
    aspect: "Site Temporary electrical installations",
    scope:
      "Have these been inspected and tested to verify compliance with BS7671 and section 6 of SP-006 TTSL LV Electrical Safety Rules",
  },
];

const DEFAULT_FORM_DATA = {
  project: "",
  serialNo: "",
  siteLocation: "",
  inspectionDate: "",
  ttslResponsiblePerson: "",
  ttslAuthorisedPersonE: "",
  projectNo: "",
  checklist: [],
  inspectionFindingsComments: "",
  remedialActions: "",
  inspectedBy: "",
  inspectedByPosition: "",
  signedTtslAuthorisedPersonE: "",
  inspectedDate: "",
  auditedBy: "",
  auditedByPosition: "",
  signedTtslAuthorisingEngineer: "",
  auditedDate: "",
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function normalizeInspectionMark(val) {
  if (val === true || val === false) return val;
  const s = String(val ?? "").trim().toLowerCase();
  if (s === "true" || s === "tick" || s === "✔" || s === "yes" || s === "1") return true;
  if (s === "false" || s === "cross" || s === "x" || s === "no" || s === "0") return false;
  return "";
}

function defaultChecklist() {
  return AUDIT_CHECKLIST_TEMPLATE.map((tpl) => ({
    itemNo: tpl.itemNo,
    aspect: tpl.aspect,
    scope: tpl.scope,
    inspectionMark: "",
  }));
}

function emptyChecklistRow(itemNo) {
  return {
    itemNo: itemNo ?? "",
    aspect: "",
    scope: "",
    inspectionMark: "",
  };
}

function normalizeChecklistRow(row, idx) {
  const src = row && typeof row === "object" ? row : {};
  const itemNo = Number(src.itemNo ?? src.item ?? src.no) || idx + 1;
  const tpl = AUDIT_CHECKLIST_TEMPLATE.find((t) => t.itemNo === itemNo);
  let mark = src.inspectionMark;
  if (mark === "" || mark == null) {
    if (src.tick === true) mark = true;
    else if (src.cross === true) mark = false;
  }
  const aspectFromRow = src.aspect != null ? String(src.aspect) : null;
  const scopeFromRow = src.scope != null ? String(src.scope) : null;
  return {
    itemNo,
    aspect: (aspectFromRow !== null ? aspectFromRow : tpl?.aspect || "").trim(),
    scope: (scopeFromRow !== null ? scopeFromRow : tpl?.scope || "").trim(),
    inspectionMark: normalizeInspectionMark(mark),
  };
}

function normalizeChecklist(srcList) {
  if (!Array.isArray(srcList) || srcList.length === 0) return defaultChecklist();
  return srcList.map((row, idx) => normalizeChecklistRow(row, idx));
}

function cloneChecklist(list) {
  return normalizeChecklist(list).map((r) => ({ ...r }));
}

function hasChecklistRowData(row) {
  if (!row) return false;
  return (
    normalizeInspectionMark(row.inspectionMark) ||
    String(row.aspect || "").trim() ||
    String(row.scope || "").trim()
  );
}

function checklistForPdf(list) {
  const rows = cloneChecklist(list);
  const maxNo = rows.reduce((max, r) => Math.max(max, Number(r.itemNo) || 0), 0);
  for (let i = 0; i < PDF_EXTRA_BLANK_ROWS; i++) {
    rows.push(emptyChecklistRow(maxNo + i + 1));
  }
  return rows;
}

function checklistForSave(list) {
  return cloneChecklist(list).filter(hasChecklistRowData);
}

function normalizeDescriptionField(val) {
  if (val == null) return "";
  if (Array.isArray(val)) {
    return val
      .map((item) => {
        if (typeof item === "object" && item != null) return String(item.text ?? item.comment ?? "").trim();
        return String(item ?? "").trim();
      })
      .filter(Boolean)
      .join("\n");
  }
  return String(val).trim();
}

function mergeRecordIntoDefaults(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const hd = src.headerDetails || {};
  return {
    project: src.project ?? hd.project ?? src.projectName ?? "",
    serialNo: src.serialNo ?? hd.serialNo ?? "",
    siteLocation: src.siteLocation ?? hd.siteLocation ?? src.site ?? "",
    inspectionDate: src.inspectionDate ?? hd.inspectionDate ?? src.date ?? "",
    ttslResponsiblePerson: src.ttslResponsiblePerson ?? hd.ttslResponsiblePerson ?? "",
    ttslAuthorisedPersonE: src.ttslAuthorisedPersonE ?? hd.ttslAuthorisedPersonE ?? hd.ttslAuthorisedPerson ?? "",
    projectNo: src.projectNo ?? hd.projectNo ?? "",
    checklist: normalizeChecklist(src.checklist ?? src.auditChecklist),
    inspectionFindingsComments: normalizeDescriptionField(
      src.inspectionFindingsComments ?? src.inspectionFindings ?? src.inspection_findings_comments
    ),
    remedialActions: normalizeDescriptionField(src.remedialActions ?? src.remedialAction ?? src.remedial_action),
    inspectedBy: src.inspectedBy ?? "",
    inspectedByPosition: src.inspectedByPosition ?? "",
    signedTtslAuthorisedPersonE: src.signedTtslAuthorisedPersonE ?? src.signatureAuthorisedPersonE ?? "",
    inspectedDate: src.inspectedDate ?? "",
    auditedBy: src.auditedBy ?? "",
    auditedByPosition: src.auditedByPosition ?? "",
    signedTtslAuthorisingEngineer: src.signedTtslAuthorisingEngineer ?? src.signatureAuthorisingEngineer ?? "",
    auditedDate: src.auditedDate ?? "",
  };
}

const sf009TblBorder = `1px solid ${THEME_BORDER}`;
const sf009LblCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: THEME,
  color: "#fff",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
const sf009HdrLblWrap = { ...sf009LblCell, whiteSpace: "normal", lineHeight: 1.35, fontSize: 11 };
const sf009ValCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 12,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009RegHeadCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 10,
  fontWeight: 600,
  background: THEME_LIGHT,
  color: THEME,
  textAlign: "center",
  verticalAlign: "middle",
};
const sf009RegCell = {
  border: sf009TblBorder,
  padding: "4px 6px",
  fontSize: 11,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009CellInput = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 11,
  padding: "4px 2px",
  background: "transparent",
  boxSizing: "border-box",
};
const sf009MarkCell = {
  ...sf009RegCell,
  textAlign: "center",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 14,
  minWidth: 36,
  userSelect: "none",
};

function Sf009HElectricalAuditInspectionReportPage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  useEffect(() => {
    fetchForms();
    fetchAppUsers();
  }, []);

  const getRawFormData = getRawSf009FormData;

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + API_PATH, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = forms.filter((f) => {
    const fd = mergeRecordIntoDefaults(getRawFormData(f));
    const fields = [fd.project, fd.serialNo, fd.siteLocation, fd.ttslResponsiblePerson, f.name].map((v) =>
      (v || "").toLowerCase()
    );
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData, [key]: value } };
      if (key === "ttslResponsiblePerson" || key === "inspectionDate") {
        next.name = next.formData.ttslResponsiblePerson || "";
        next.date = next.formData.inspectionDate || "";
      }
      return next;
    });
  };

  const updateChecklistRow = (idx, field, value) => {
    setEditForm((p) => {
      const checklist = cloneChecklist(p.formData.checklist);
      checklist[idx] = { ...checklist[idx], [field]: value };
      return { ...p, formData: { ...p.formData, checklist } };
    });
  };

  const setChecklistMark = (idx, mark) => {
    setEditForm((p) => {
      const checklist = cloneChecklist(p.formData.checklist);
      const cur = checklist[idx]?.inspectionMark;
      checklist[idx] = { ...checklist[idx], inspectionMark: cur === mark ? "" : mark };
      return { ...p, formData: { ...p.formData, checklist } };
    });
  };

  const addChecklistRow = () => {
    setEditForm((p) => {
      const checklist = cloneChecklist(p.formData.checklist);
      const nextNo = checklist.reduce((max, r) => Math.max(max, Number(r.itemNo) || 0), 0) + 1;
      checklist.push(emptyChecklistRow(nextNo));
      return { ...p, formData: { ...p.formData, checklist } };
    });
  };

  const removeChecklistRow = (idx) => {
    if (idx < AUDIT_CHECKLIST_TEMPLATE.length) return;
    setEditForm((p) => {
      const checklist = cloneChecklist(p.formData.checklist);
      checklist.splice(idx, 1);
      return { ...p, formData: { ...p.formData, checklist: checklist.length ? checklist : defaultChecklist() } };
    });
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    const merged = mergeRecordIntoDefaults(raw);
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      name: f.name || merged.ttslResponsiblePerson || "",
      date: f.date || merged.inspectionDate || "",
      formData: merged,
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const raw = { ...editForm.formData };
      raw.checklist = checklistForSave(raw.checklist);
      raw.inspectionFindingsComments = normalizeDescriptionField(raw.inspectionFindingsComments);
      raw.remedialActions = normalizeDescriptionField(raw.remedialActions);
      const res = await fetch(getBaseUrl() + API_PATH + "/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...raw,
          name: editForm.name || raw.ttslResponsiblePerson,
          date: editForm.date || raw.inspectionDate,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + API_PATH + "/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        toast.success("Form deleted");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const parsed = parseSf009DateForPicker(d);
    if (parsed) return parsed.toLocaleDateString("en-GB");
    try {
      return new Date(d).toLocaleDateString("en-GB");
    } catch {
      return String(d);
    }
  };

  const getUserName = (record) => getSf009AddedByDisplay(record, appUsers);

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;

    const recordRaw = getRawFormData(record);
    const raw = mergeRecordIntoDefaults(recordRaw);
    const checklist = checklistForPdf(raw.checklist);
    const findingsText = normalizeDescriptionField(raw.inspectionFindingsComments);
    const remedialText = normalizeDescriptionField(raw.remedialActions);

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentX = 10;
    const contentW = pageWidth - 20;
    const pdfMargin = { left: contentX, right: contentX, bottom: PDF_PAGE_BOTTOM };
    const headerHeight = 30;

    const safePdfDate = (v) => {
      if (!v) return "";
      const d = parseSf009DateForPicker(v);
      if (!d) return String(v);
      return d.toLocaleDateString("en-GB");
    };

    doc.setFillColor(...THEME_RGB);
    doc.rect(0, 0, pageWidth, headerHeight, "F");
    try {
      const logoImg = new Image();
      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.onerror = resolve;
        logoImg.src = onsiteproLogo;
      });
      doc.addImage(logoImg, "PNG", 10, 5, 65, 20);
    } catch {}

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Electrical Audit Inspection Report", 90, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", 90, 19);

    let yPosition = headerHeight + 8;

    const pdfGrid = {
      font: "helvetica",
      fontSize: 7.5,
      cellPadding: 2.5,
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    };
    const pdfLbl = { fillColor: THEME_RGB, fontStyle: "bold", textColor: [255, 255, 255], fontSize: 7.5 };
    const pdfVal = { fillColor: PDF_WHITE, fontSize: 7.5 };
    const pdfRegHead = {
      fillColor: THEME_LIGHT_RGB,
      fontStyle: "bold",
      textColor: THEME_RGB,
      fontSize: 7,
      halign: "center",
      valign: "middle",
    };
    const pdfRegBody = { fillColor: PDF_WHITE, fontSize: 7, valign: "middle", minCellHeight: 10 };
    const pdfHdrCol = {
      0: { cellWidth: contentW * 0.22, ...pdfLbl },
      1: { cellWidth: contentW * 0.28, ...pdfVal },
      2: { cellWidth: contentW * 0.22, ...pdfLbl },
      3: { cellWidth: contentW * 0.28, ...pdfVal },
    };
    const checklistColW = {
      0: { cellWidth: contentW * 0.06, halign: "center" },
      1: { cellWidth: contentW * 0.22 },
      2: { cellWidth: contentW * 0.58 },
      3: { cellWidth: contentW * 0.07, halign: "center", minCellHeight: 10 },
      4: { cellWidth: contentW * 0.07, halign: "center", minCellHeight: 10 },
    };
    const pdfTableHeadStyles = {
      fillColor: THEME_LIGHT_RGB,
      textColor: THEME_RGB,
      fontStyle: "bold",
      fontSize: 6.5,
      halign: "center",
      valign: "middle",
      lineWidth: 0.2,
    };

    const ensureSpace = (needed = 20) => {
      if (yPosition + needed > pageHeight - PDF_PAGE_BOTTOM) {
        doc.addPage();
        yPosition = headerHeight + 8;
      }
    };

    const addPdfTable = (tableConfig, spaceNeeded = 20) => {
      ensureSpace(spaceNeeded);
      autoTable(doc, {
        theme: "grid",
        tableWidth: contentW,
        margin: pdfMargin,
        rowPageBreak: "auto",
        ...tableConfig,
        startY: yPosition,
      });
      yPosition = doc.lastAutoTable.finalY;
    };

    addPdfTable(
      {
        body: [
          ["Project :", raw.project || "", "Serial No.", raw.serialNo || ""],
          ["Site/Location:", raw.siteLocation || "", "Inspection Date:", safePdfDate(raw.inspectionDate)],
          [
            "TTSL Responsible Person:",
            raw.ttslResponsiblePerson || "",
            "TTSL Authorised Person (E):",
            raw.ttslAuthorisedPersonE || "",
          ],
          ["Project No:", raw.projectNo || "", "", ""],
        ],
        columnStyles: pdfHdrCol,
        styles: pdfGrid,
      },
      28
    );

    const checklistBody = checklist.map((row) => [
      String(row.itemNo),
      row.aspect || "",
      row.scope || "",
      row.inspectionMark === true ? " " : "",
      row.inspectionMark === false ? " " : "",
    ]);

    if (checklistBody.length > 0) {
      ensureSpace(40);
      autoTable(doc, {
        startY: yPosition,
        theme: "grid",
        tableWidth: contentW,
        margin: pdfMargin,
        rowPageBreak: "auto",
        showHead: "firstPage",
        head: [
          [
            { content: "Check list of electrical aspects:", colSpan: 3, styles: pdfRegHead },
            { content: "Enter tick against aspects inspected:", colSpan: 2, styles: pdfRegHead },
          ],
          [
            { content: "Item", styles: pdfRegHead },
            { content: "Aspect", styles: pdfRegHead },
            { content: "Scope", styles: pdfRegHead },
            { content: "✔", styles: pdfRegHead },
            { content: "X", styles: pdfRegHead },
          ],
        ],
        body: checklistBody,
        styles: pdfGrid,
        bodyStyles: pdfRegBody,
        headStyles: pdfTableHeadStyles,
        columnStyles: checklistColW,
        didDrawCell: (data) => {
          if (data.section !== "body") return;
          const item = checklist[data.row.index];
          if (!item) return;
          const iconSize = 3.4;
          const iconX = data.cell.x + data.cell.width / 2 - iconSize / 2;
          const iconY = data.cell.y + data.cell.height / 2 - iconSize / 2;
          if (data.column.index === 3 && item.inspectionMark === true) {
            try {
              doc.addImage(CheckTick, "PNG", iconX, iconY, iconSize, iconSize);
            } catch {}
          }
          if (data.column.index === 4 && item.inspectionMark === false) {
            drawPdfCrossIcon(doc, iconX, iconY, iconSize);
          }
        },
      });
      yPosition = doc.lastAutoTable.finalY;
    }

    addPdfTable(
      {
        body: [[{ content: "Inspection Findings and Comments", colSpan: 4, styles: { ...pdfRegHead, halign: "center" } }]],
        columnStyles: { 0: { cellWidth: contentW } },
        styles: pdfGrid,
      },
      14
    );
    addPdfTable(
      {
        body: [[findingsText || ""]],
        columnStyles: { 0: { cellWidth: contentW, ...pdfVal } },
        styles: { ...pdfGrid, minCellHeight: 22, fontSize: 7.5 },
      },
      28
    );

    addPdfTable(
      {
        body: [[{ content: "Remedial Action", colSpan: 4, styles: { ...pdfRegHead, halign: "center" } }]],
        columnStyles: { 0: { cellWidth: contentW } },
        styles: pdfGrid,
      },
      14
    );
    addPdfTable(
      {
        body: [[remedialText || ""]],
        columnStyles: { 0: { cellWidth: contentW, ...pdfVal } },
        styles: { ...pdfGrid, minCellHeight: 22, fontSize: 7.5 },
      },
      28
    );

    addPdfTable(
      {
        body: [
          ["Inspected by:", raw.inspectedBy || "", "Position:", raw.inspectedByPosition || ""],
          [
            "Signed TTSL Authorised Person (E):",
            isSigImage(raw.signedTtslAuthorisedPersonE) ? " " : "",
            "Date:",
            safePdfDate(raw.inspectedDate),
          ],
          ["Audited by:", raw.auditedBy || "", "Position:", raw.auditedByPosition || ""],
          [
            "Signed TTSL Authorising Engineer:",
            isSigImage(raw.signedTtslAuthorisingEngineer) ? " " : "",
            "Date:",
            safePdfDate(raw.auditedDate),
          ],
        ],
        columnStyles: {
          ...pdfHdrCol,
          1: { ...pdfHdrCol[1], minCellHeight: 24 },
        },
        styles: pdfGrid,
        didDrawCell: (data) => {
          if (data.row.index === 1) {
            drawSignatureImageInPdfCell(
              doc,
              data,
              isSigImage(raw.signedTtslAuthorisedPersonE) ? toSigDataUrl(raw.signedTtslAuthorisedPersonE) : ""
            );
          }
          if (data.row.index === 3) {
            drawSignatureImageInPdfCell(
              doc,
              data,
              isSigImage(raw.signedTtslAuthorisingEngineer) ? toSigDataUrl(raw.signedTtslAuthorisingEngineer) : ""
            );
          }
        },
      },
      40
    );

    addPdfTable(
      {
        body: [
          [
            {
              content:
                "Ensure a copy of this audit inspection report is saved in the site folder and uploaded to the Project folder on SharePoint",
              styles: {
                fillColor: PDF_WHITE,
                fontStyle: "bold",
                fontSize: 7.5,
                textColor: [192, 0, 0],
                halign: "center",
              },
            },
          ],
        ],
        columnStyles: { 0: { cellWidth: contentW } },
        styles: pdfGrid,
      },
      18
    );

    applyFooterAllPages(doc);

    const displayName = raw.ttslResponsiblePerson || record.name || "Audit";
    const safeSite = String(raw.project || raw.siteLocation || "Site").replace(/[\\/:"*?<>|]+/g, "-");
    const safeName = String(displayName).replace(/[\\/:"*?<>|]+/g, "-");
    const safeDateStr = String(formatDate(record.date || raw.inspectionDate) || "form").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("H", getSf009SharePointFields("H", raw, record, getUserName))
        : `SF009H_${safeName}_${safeDateStr}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${raw.project || ""}|${record.date || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-H Electrical Audit Inspection Report PDF generated successfully!");
    }
  };

  const fd = editForm?.formData || DEFAULT_FORM_DATA;
  const checklist = cloneChecklist(fd.checklist);
  const safeDate = parseSf009DateForPicker;

  const renderSigDisplay = (fieldKey, label) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: THEME, marginBottom: 4 }}>{label}</div>
      <div
        style={{
          border: `1px solid ${THEME_BORDER}`,
          borderRadius: 8,
          background: "#fafbfc",
          overflow: "hidden",
          minHeight: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4px 6px",
        }}
      >
        <Sf009SignatureDisplay value={fd[fieldKey]} />
      </div>
    </div>
  );

  const renderAuditForm = () => (
    <div style={{ border: sf009TblBorder, background: "#fff" }}>
      <div style={{ textAlign: "center", padding: "14px 12px", fontWeight: 700, fontSize: 15, color: THEME, borderBottom: sf009TblBorder }}>
        Electrical Audit Inspection Report
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={sf009LblCell}>Project :</td>
            <td colSpan={3} style={sf009ValCell}>
              <input type="text" value={fd.project || ""} onChange={(e) => updateFormData("project", e.target.value)} style={sf009CellInput} />
            </td>
            <td style={sf009LblCell}>Serial No.</td>
            <td style={sf009ValCell}>
              <input type="text" value={fd.serialNo || ""} onChange={(e) => updateFormData("serialNo", e.target.value)} style={sf009CellInput} />
            </td>
          </tr>
          <tr>
            <td style={sf009LblCell}>Site/Location:</td>
            <td colSpan={3} style={sf009ValCell}>
              <input type="text" value={fd.siteLocation || ""} onChange={(e) => updateFormData("siteLocation", e.target.value)} style={sf009CellInput} />
            </td>
            <td style={sf009LblCell}>Inspection Date:</td>
            <td style={sf009ValCell}>
              <DatePicker
                selected={safeDate(fd.inspectionDate)}
                onChange={(d) => updateFormData("inspectionDate", d ? datePickerToSf009DateString(d) : "")}
                dateFormat="dd-MM-yyyy"
                className="form-control form-control-sm border-0 p-0"
                placeholderText="dd-MM-yyyy"
                wrapperClassName="w-100"
              />
            </td>
          </tr>
          <tr>
            <td style={sf009HdrLblWrap}>TTSL Responsible Person:</td>
            <td style={sf009ValCell}>
              <input
                type="text"
                value={fd.ttslResponsiblePerson || ""}
                onChange={(e) => updateFormData("ttslResponsiblePerson", e.target.value)}
                style={sf009CellInput}
              />
            </td>
            <td style={sf009HdrLblWrap}>TTSL Authorised Person (E):</td>
            <td style={sf009ValCell}>
              <input
                type="text"
                value={fd.ttslAuthorisedPersonE || ""}
                onChange={(e) => updateFormData("ttslAuthorisedPersonE", e.target.value)}
                style={sf009CellInput}
              />
            </td>
            <td style={sf009LblCell}>Project No:</td>
            <td style={sf009ValCell}>
              <input type="text" value={fd.projectNo || ""} onChange={(e) => updateFormData("projectNo", e.target.value)} style={sf009CellInput} />
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 0 }}>
          <thead>
            <tr>
              <th colSpan={3} style={sf009RegHeadCell}>
                Check list of electrical aspects:
              </th>
              <th colSpan={3} style={sf009RegHeadCell}>
                Enter tick against aspects inspected:
              </th>
            </tr>
            <tr>
              <th style={sf009RegHeadCell}>Item</th>
              <th style={sf009RegHeadCell}>Aspect</th>
              <th style={sf009RegHeadCell}>Scope</th>
              <th style={sf009RegHeadCell}>✔</th>
              <th style={sf009RegHeadCell}>X</th>
              <th style={{ ...sf009RegHeadCell, width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {checklist.map((row, idx) => (
              <tr key={`${idx}-${row.itemNo}`}>
                <td style={{ ...sf009RegCell, textAlign: "center", width: 48 }}>
                  <input
                    type="number"
                    min={1}
                    value={row.itemNo ?? ""}
                    onChange={(e) => updateChecklistRow(idx, "itemNo", e.target.value ? Number(e.target.value) : "")}
                    style={{ ...sf009CellInput, textAlign: "center", fontWeight: 600 }}
                  />
                </td>
                <td style={sf009RegCell}>
                  <input
                    type="text"
                    value={row.aspect || ""}
                    onChange={(e) => updateChecklistRow(idx, "aspect", e.target.value)}
                    style={sf009CellInput}
                  />
                </td>
                <td style={sf009RegCell}>
                  <textarea
                    value={row.scope || ""}
                    onChange={(e) => updateChecklistRow(idx, "scope", e.target.value)}
                    rows={2}
                    style={{ ...sf009CellInput, resize: "vertical", minHeight: 36 }}
                  />
                </td>
                <td
                  style={{
                    ...sf009MarkCell,
                    background: row.inspectionMark === true ? THEME_LIGHT : "#fff",
                    color: THEME,
                  }}
                  onClick={() => setChecklistMark(idx, true)}
                >
                  {row.inspectionMark === true ? "✓" : ""}
                </td>
                <td
                  style={{
                    ...sf009MarkCell,
                    background: row.inspectionMark === false ? THEME_LIGHT : "#fff",
                    color: THEME,
                  }}
                  onClick={() => setChecklistMark(idx, false)}
                >
                  {row.inspectionMark === false ? "✕" : ""}
                </td>
                <td style={{ ...sf009RegCell, textAlign: "center" }}>
                  {idx >= AUDIT_CHECKLIST_TEMPLATE.length ? (
                    <button
                      type="button"
                      onClick={() => removeChecklistRow(idx)}
                      title="Remove row"
                      style={{ background: "transparent", border: "none", color: "#e53e3e", cursor: "pointer", padding: 4 }}
                    >
                      <i className="fa fa-trash" />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: "8px", textAlign: "right" }}>
          <button
            type="button"
            onClick={addChecklistRow}
            style={{
              padding: "6px 12px",
              fontSize: 11,
              fontWeight: 600,
              background: THEME,
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            + Add checklist row
          </button>
        </div>
      </div>

      <div style={{ padding: "12px 8px 0" }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: THEME, textAlign: "center", marginBottom: 8, background: THEME_LIGHT, padding: 8, border: sf009TblBorder }}>
          Inspection Findings and Comments
        </div>
        <textarea
          value={fd.inspectionFindingsComments || ""}
          onChange={(e) => updateFormData("inspectionFindingsComments", e.target.value)}
          rows={5}
          style={{
            width: "100%",
            fontSize: 11,
            border: `1px solid ${THEME_BORDER}`,
            borderRadius: 6,
            padding: 8,
            marginBottom: 16,
            boxSizing: "border-box",
            resize: "vertical",
          }}
        />

        <div style={{ fontWeight: 700, fontSize: 12, color: THEME, textAlign: "center", marginBottom: 8, background: THEME_LIGHT, padding: 8, border: sf009TblBorder }}>
          Remedial Action
        </div>
        <textarea
          value={fd.remedialActions || ""}
          onChange={(e) => updateFormData("remedialActions", e.target.value)}
          rows={5}
          style={{
            width: "100%",
            fontSize: 11,
            border: `1px solid ${THEME_BORDER}`,
            borderRadius: 6,
            padding: 8,
            marginBottom: 16,
            boxSizing: "border-box",
            resize: "vertical",
          }}
        />
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={sf009LblCell}>Inspected by:</td>
            <td style={sf009ValCell}>
              <input type="text" value={fd.inspectedBy || ""} onChange={(e) => updateFormData("inspectedBy", e.target.value)} style={sf009CellInput} />
            </td>
            <td style={sf009LblCell}>Position:</td>
            <td style={sf009ValCell}>
              <input type="text" value={fd.inspectedByPosition || ""} onChange={(e) => updateFormData("inspectedByPosition", e.target.value)} style={sf009CellInput} />
            </td>
          </tr>
          <tr>
            <td colSpan={2} style={sf009ValCell}>
              {renderSigDisplay("signedTtslAuthorisedPersonE", "Signed TTSL Authorised Person (E):")}
            </td>
            <td style={sf009LblCell}>Date:</td>
            <td style={sf009ValCell}>
              <DatePicker
                selected={safeDate(fd.inspectedDate)}
                onChange={(d) => updateFormData("inspectedDate", d ? datePickerToSf009DateString(d) : "")}
                dateFormat="dd-MM-yyyy"
                className="form-control form-control-sm border-0 p-0"
                placeholderText="dd-MM-yyyy"
                wrapperClassName="w-100"
              />
            </td>
          </tr>
          <tr>
            <td style={sf009LblCell}>Audited by:</td>
            <td style={sf009ValCell}>
              <input type="text" value={fd.auditedBy || ""} onChange={(e) => updateFormData("auditedBy", e.target.value)} style={sf009CellInput} />
            </td>
            <td style={sf009LblCell}>Position:</td>
            <td style={sf009ValCell}>
              <input type="text" value={fd.auditedByPosition || ""} onChange={(e) => updateFormData("auditedByPosition", e.target.value)} style={sf009CellInput} />
            </td>
          </tr>
          <tr>
            <td colSpan={2} style={sf009ValCell}>
              {renderSigDisplay("signedTtslAuthorisingEngineer", "Signed TTSL Authorising Engineer:")}
            </td>
            <td style={sf009LblCell}>Date:</td>
            <td style={sf009ValCell}>
              <DatePicker
                selected={safeDate(fd.auditedDate)}
                onChange={(d) => updateFormData("auditedDate", d ? datePickerToSf009DateString(d) : "")}
                dateFormat="dd-MM-yyyy"
                className="form-control form-control-sm border-0 p-0"
                placeholderText="dd-MM-yyyy"
                wrapperClassName="w-100"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <p style={{ color: "#c53030", fontWeight: 700, fontSize: 11, padding: "12px 10px", margin: 0 }}>
        Ensure a copy of this audit inspection report is saved in the site folder and uploaded to the Project folder on SharePoint
      </p>
    </div>
  );

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-009-H Electrical Audit Inspection Report</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{ background: THEME, color: "#fff", border: "none", borderRadius: 10, padding: "10px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
              >
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Project</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Serial No</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Site / Location</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Inspection Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Responsible Person</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const raw = mergeRecordIntoDefaults(getRawFormData(r));
                  const updatedDate = r.updatedAt || r.createdAt || r.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.project || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.serialNo || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.siteLocation || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(raw.inspectionDate || r.date)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.ttslResponsiblePerson || r.name || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${raw.project || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    generatePDF(r, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
            Next
          </button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 980, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-009-H Electrical Audit Inspection Report</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>
                {renderAuditForm()}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-H Electrical Audit Inspection Report File"
        importPath="sf-009-h-electrical-audit-inspection-report/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-H Electrical Audit Inspection Report</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sf009HElectricalAuditInspectionReportPage;
